import {
  BrandLogo,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ThemeScope,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { BRAND } from '../lib/config';

export function LoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Any arrival at the login screen means there is no valid session — wipe the query
  // cache so a subsequent sign-in (possibly a different operator after an expiry
  // redirect) provably starts from empty. This is what lets AppShell's identity armor
  // skip the wipe on the null → user transition without leaking cross-user data.
  useEffect(() => {
    if (!state.authenticated) qc.clear();
  }, [state.authenticated, qc]);

  // Post-login landing is the overview, not the sale list — it is the page that answers
  // "what is running right now" without making the operator read three tabs.
  useEffect(() => {
    if (state.authenticated) void navigate({ to: '/' });
  }, [state.authenticated, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      void navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-hero px-4">
      {/* Login is a fixed-dark surface (docs/15 §5) over the hero gradient. */}
      <ThemeScope theme="dark" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <BrandLogo variant="white" alt={BRAND} className="mb-2" />
          <CardTitle className="font-head text-display font-semibold">
            {t('login.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p
                role="alert"
                className="flex items-center gap-2 rounded-md bg-danger-tint px-3 py-2 text-sm text-danger-tint-fg"
              >
                <AlertCircle aria-hidden className="h-4 w-4 shrink-0" />
                {error}
              </p>
            )}
            {/* The one gradient CTA of the view (docs/15 §2 + redesign OQ-4). */}
            <Button type="submit" variant="brand" className="w-full" disabled={busy}>
              {busy ? t('login.submitting') : t('login.submit')}
            </Button>
            {/* M19: before this, a forgotten admin password was an operator ticket. */}
            <Link
              to="/forgot-password"
              className="block rounded-sm text-center text-sm text-muted-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('reset.forgotLink')}
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
